import { useEffect, useState } from "react";
import { Download, X, Share2, Plus, Smartphone } from "lucide-react";

const DISMISS_KEY = "imaggio_install_dismissed_at";
const DISMISS_DAYS = 7;

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [device, setDevice] = useState("android"); // "ios" | "android" | "desktop"
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const daysSince = (Date.now() - parseInt(dismissed, 10)) / (1000 * 60 * 60 * 24);
        if (daysSince < DISMISS_DAYS) return;
      }
    } catch {}

    let iosDetected = false;
    let androidDetected = false;
    let isInStandalone = false;
    try {
      const ua = window.navigator.userAgent || "";
      iosDetected = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      androidDetected = /Android/.test(ua);
      isInStandalone =
        (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
        window.navigator.standalone === true;
    } catch {}

    if (isInStandalone) return; // already installed

    if (iosDetected) {
      setDevice("ios");
      setShow(true);
      return;
    }
    if (androidDetected) setDevice("android");
    else setDevice("desktop");

    // Android / Desktop Chrome: listen for the install prompt event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setShow(false));

    // If beforeinstallprompt doesn't fire within 3s on mobile, show the banner
    // anyway with manual instructions (Firefox mobile, Samsung Internet, etc.)
    const t = setTimeout(() => {
      if (!isInStandalone && (androidDetected || iosDetected)) setShow(true);
    }, 3000);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      clearTimeout(t);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) { setExpanded(true); return; }
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setShow(false);
    } catch (e) {
      console.error("Install prompt failed:", e);
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 sm:left-auto sm:right-4 sm:w-96 z-[60] rounded-md shadow-2xl border border-[hsl(var(--gold))] bg-[#0F0D0A] text-white overflow-hidden"
      data-testid="install-prompt"
    >
      <div className="p-4 flex items-start gap-3">
        <img src="/logo.png" alt="IMAGGIO" className="w-12 h-12 object-contain shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-brand text-lg leading-tight text-[#D4B77E]" style={{ letterSpacing: "0.08em" }}>
            Instalează aplicația
          </p>
          {device === "ios" && (
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              Apasă <b>Share</b> <Share2 className="inline-block w-3 h-3 -mt-0.5" /> în Safari, apoi <b>„Adaugă pe ecranul principal"</b> <Plus className="inline-block w-3 h-3 -mt-0.5" />.
            </p>
          )}
          {device !== "ios" && (
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              Adaugă IMAGGIO pe telefon/desktop. Se deschide ca aplicație nativă — offline, notificări, viteză maximă.
            </p>
          )}
          {device !== "ios" && (
            <button
              onClick={install}
              className="mt-2 inline-flex items-center gap-1.5 bg-[hsl(var(--gold))] hover:bg-[hsl(var(--gold-deep))] text-[hsl(var(--charcoal))] hover:text-white text-xs font-semibold px-3 py-2 rounded-sm transition-colors"
              data-testid="install-prompt-btn"
            >
              <Download className="w-3.5 h-3.5" /> Instalează acum
            </button>
          )}
          {device === "android" && !deferredPrompt && expanded && (
            <div className="mt-2 text-[11px] text-white/70 leading-relaxed space-y-1 border-t border-white/10 pt-2">
              <p><b>Android — dacă butonul nu declanșează popup:</b></p>
              <p>1. Deschide meniul Chrome (⋮ sus-dreapta)</p>
              <p>2. Apasă „Instalează aplicația" sau „Adaugă pe ecranul de start"</p>
              <p>3. Confirmă „Instalează"</p>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-white/50 hover:text-white -mr-1 -mt-1 p-1"
          aria-label="Închide"
          data-testid="install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
